import React, { useState } from "react";
import { ArrowLeft, User, Check, CheckCheck, Send } from "lucide-react";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAppContext } from "../../context/AppContext";

export default function DmRoom({
  activeDmRoom,
  setActiveDmRoom,
  inboxMessages,
  setSelectedChatUser,
}: any) {
  const { t, user, activePet } = useAppContext();
  const [dmInput, setDmInput] = useState("");
  const [dmReactionPopup, setDmReactionPopup] = useState<
    string | number | null
  >(null);

  const handleSendDmMessage = async () => {
    if (!dmInput.trim() || !user || !activeDmRoom) return;
    const text = dmInput;
    setDmInput("");
    await addDoc(collection(db, "direct_messages"), {
      roomId: activeDmRoom.id,
      senderId: user.uid,
      senderName: activePet.name,
      senderPhoto: activePet.photo || null,
      recipientId: activeDmRoom.otherUserId,
      recipientName: activeDmRoom.otherUserName,
      recipientPhoto: activeDmRoom.otherUserPhoto,
      text,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      createdAt: serverTimestamp(),
      read: false,
      senderBreed: activePet.breed || "",
      senderGender: activePet.gender || "",
      senderDob: activePet.dob || "",
    });
  };

  const handleDmReaction = async (msgId: string | number, emoji: string) => {
    if (!user) return;
    const msgRef = doc(db, "direct_messages", String(msgId));
    const msgSnap = await getDoc(msgRef);
    if (!msgSnap.exists()) return;
    const data = msgSnap.data();
    const currentReactions = data.reactions || {};
    const usersForEmoji = currentReactions[emoji] || [];
    if (usersForEmoji.includes(user.uid)) {
      currentReactions[emoji] = usersForEmoji.filter(
        (id: string) => id !== user.uid
      );
      if (currentReactions[emoji].length === 0) delete currentReactions[emoji];
    } else currentReactions[emoji] = [...usersForEmoji, user.uid];
    await updateDoc(msgRef, { reactions: currentReactions });
    setDmReactionPopup(null);
  };

  return (
    <div
      className="fixed inset-0 bg-[var(--bg-main)] z-[500] animate-fade-in flex flex-col"
      onClick={() => setDmReactionPopup(null)}
    >
      <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-5 py-4 shrink-0 pt-12 shadow-sm relative z-40 flex items-center gap-3">
        <button
          onClick={() => setActiveDmRoom(null)}
          className="active:scale-95 text-[var(--text-main)]"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className="w-8 h-8 rounded-full bg-[var(--accent-soft)] overflow-hidden shrink-0 border border-[var(--border-color)] flex items-center justify-center cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedChatUser({
                id: activeDmRoom.otherUserId,
                name: activeDmRoom.otherUserName,
                photo: activeDmRoom.otherUserPhoto,
              });
            }}
          >
            {activeDmRoom.otherUserPhoto ? (
              <img
                src={activeDmRoom.otherUserPhoto}
                className="w-full h-full object-cover"
              />
            ) : (
              <User size={16} className="text-[#a073e7]" />
            )}
          </div>
          <h2 className="font-extrabold text-lg text-[var(--text-main)] truncate">
            {activeDmRoom.otherUserName}
          </h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6 pb-32">
        {inboxMessages
          .filter((m: any) => m.roomId === activeDmRoom.id)
          .map((msg: any) => (
            <div
              key={msg.id}
              className={`flex w-full ${
                msg.senderId === user?.uid ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`flex gap-2 max-w-[85%] ${
                  msg.senderId === user?.uid ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <div className="flex flex-col items-center shrink-0">
                  <span className="text-[10px] text-[var(--text-muted)] font-bold mb-1">
                    {msg.senderId === user?.uid ? t.you : msg.senderName}
                  </span>
                  <div
                    className="w-10 h-10 rounded-full bg-[var(--accent-soft)] border-2 border-[var(--bg-card)] shadow-sm flex items-center justify-center font-bold text-[#a073e7] overflow-hidden cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedChatUser({
                        id: msg.senderId,
                        name: msg.senderName,
                        photo: msg.senderPhoto,
                      });
                    }}
                  >
                    {msg.senderPhoto ? (
                      <img
                        src={msg.senderPhoto}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      msg.senderName?.[0]
                    )}
                  </div>
                </div>
                <div
                  className={`flex flex-col pt-[18px] ${
                    msg.senderId === user?.uid ? "items-end" : "items-start"
                  }`}
                >
                  <div className="relative group">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setDmReactionPopup(
                          dmReactionPopup === msg.id ? null : msg.id
                        );
                      }}
                      className={`px-4 py-2.5 shadow-sm text-[15px] cursor-pointer ${
                        msg.senderId === user?.uid
                          ? "bg-[var(--inverted-btn)] text-[var(--inverted-text)] rounded-l-2xl rounded-tr-2xl"
                          : "bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-main)] rounded-r-2xl rounded-tl-2xl"
                      }`}
                    >
                      {msg.text}
                    </div>
                    {dmReactionPopup === msg.id && (
                      <div
                        className={`absolute top-[-40px] ${
                          msg.senderId === user?.uid ? "right-0" : "left-0"
                        } bg-[var(--bg-card)] border border-[var(--border-color)] shadow-lg rounded-full px-2 py-1 flex gap-2 z-50 animate-fade-in`}
                      >
                        {["👍", "❤️", "😂", "😢", "🔥"].map((emoji) => (
                          <button
                            key={emoji}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDmReaction(msg.id, emoji);
                            }}
                            className="hover:scale-125 transition-transform text-lg"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                      <div
                        className={`flex flex-wrap gap-1 mt-1 ${
                          msg.senderId === user?.uid
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        {Object.entries(msg.reactions).map(
                          ([emoji, users]: [string, any]) => (
                            <span
                              key={emoji}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDmReaction(msg.id, emoji);
                              }}
                              className={`text-[10px] px-1.5 py-0.5 rounded-full border cursor-pointer ${
                                users.includes(user?.uid || "")
                                  ? "bg-[var(--accent-soft)] border-[var(--border-accent)] text-[#a073e7]"
                                  : "bg-[var(--bg-card)] border-[var(--border-color)] text-[var(--text-muted)]"
                              }`}
                            >
                              {emoji} {users.length}
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                  <span className="text-[9px] text-[var(--text-muted)] mt-1 flex items-center gap-1">
                    {msg.time}{" "}
                    {msg.senderId === user?.uid &&
                      (msg.read ? (
                        <CheckCheck size={12} className="text-[#10b981]" />
                      ) : (
                        <Check size={12} />
                      ))}
                  </span>
                </div>
              </div>
            </div>
          ))}
      </div>
      <div
        className="fixed bottom-[80px] left-0 right-0 bg-[var(--bg-card)] p-3 border-t border-[var(--border-color)] z-30"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 bg-[var(--bg-soft)] border border-[var(--border-accent)] rounded-full px-1 py-1">
          <input
            value={dmInput}
            onChange={(e) => setDmInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendDmMessage()}
            placeholder={t.write}
            className="flex-1 bg-transparent px-4 outline-none text-sm h-10 text-[var(--text-main)]"
          />
          <button
            onClick={handleSendDmMessage}
            className="p-2.5 bg-[var(--inverted-btn)] rounded-full text-[var(--inverted-text)] shadow-md"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
